import { useState } from 'react'

export type SortDir = 'asc' | 'desc'
export type SortState = { key: string; dir: SortDir }

/**
 * Pure sort: returns a new array ordered by `sort`. An empty `sort.key` means
 * "no sort" — the input order is preserved (a copy is returned). Numbers sort
 * numerically, everything else by locale string. Use directly when the sort
 * state must drive more than one row list (e.g. a table with two row modes);
 * otherwise prefer the `useSortable` hook.
 */
export function sortRows<T>(
  rows: T[],
  getValue: (row: T, key: string) => number | string,
  sort: SortState,
): T[] {
  if (!sort.key) return rows.slice()
  const dir = sort.dir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const va = getValue(a, sort.key)
    const vb = getValue(b, sort.key)
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir
    return String(va).localeCompare(String(vb)) * dir
  })
}

/**
 * Client-side table sorting. Pass the rows, a value accessor keyed by column,
 * and the initial sort; get back the sorted rows plus `sortKey` / `sortDir` and
 * a `toggle(key)` to wire to headers (see `<SortHeader>`).
 *
 * Clicking the active column reverses direction; clicking a new column defaults
 * to descending for numeric columns and ascending for text (biggest-first / A→Z
 * — the usual intent). An empty initial key leaves rows in their natural order
 * until a header is clicked. Sorting applies to whatever rows are passed in, so
 * it composes with upstream filtering. Fine for the table sizes here; memoize
 * the accessor if you ever apply it to very large lists.
 */
export function useSortable<T>(
  rows: T[],
  getValue: (row: T, key: string) => number | string,
  initial: SortState,
) {
  const [sort, setSort] = useState<SortState>(initial)

  const sorted = sortRows(rows, getValue, sort)

  const toggle = (key: string) =>
    setSort((s) => {
      if (s.key === key) return { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      const sample = rows.length ? getValue(rows[0], key) : ''
      return { key, dir: typeof sample === 'number' ? 'desc' : 'asc' }
    })

  return { sorted, sortKey: sort.key, sortDir: sort.dir, toggle }
}
